import { useContext, useEffect, useState } from "react";
import * as yup from "yup";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
} from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import SelectTime from "../Time/SelectTime";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

let DEFAULT_HOURLY_START_TIME = dayjs("2018-01-01 5:00 -07:00");
let DEFAULT_HOURLY_STOP_TIME = dayjs("2018-01-01 13:30 -07:00");
let DEFAULT_HOURLY_LUNCH_DURATION = 30;

const IndustryEmployeeForm = ({
  userDetails,
  onCancel,
  handleSubmit,
  loading,
}) => {
  const navigate = useNavigate();
  const { openSnackMessage } = useContext(SnackAlertContext);

  // PHONE NUMBERS MUST BE E.164 Format
  // https://www.twilio.com/docs/glossary/what-e164
  const e164PhoneNumberRegex = /^\+[1-9]\d{1,14}$/;
  const industryEmployeeValidationSchema = yup.object().shape({
    fullName: yup.string().required("Name is required"),
    email: yup.string().email().required("Email is required"),
    phone: yup
      .string()
      .matches(
        e164PhoneNumberRegex,
        "Phone must be a valid E.164 phone number format (Ex: +14804459240)"
      ),
    role: yup.string().required("Role is required"),
    salaryType: yup
      .string("Salary Type is required")
      .required("Salary Type is required"),
  });

  const formik = useFormik({
    initialValues: {
      fullName: userDetails && userDetails.fullName ? userDetails.fullName : "",
      email: userDetails && userDetails.email ? userDetails.email : "",
      jobTitle: userDetails && userDetails.jobTitle ? userDetails.jobTitle : "",
      phone: userDetails && userDetails.phone ? userDetails.phone : "",
      role:
        userDetails && userDetails.role ? userDetails.role : "EZORDER_EMPLOYEE",

      salaryType:
        userDetails && userDetails.salaryType ? userDetails.salaryType : null,
      workStartTime:
        userDetails && userDetails.workStartTime
          ? dayjs(`2018-01-01 ${userDetails.workStartTime}`)
          : null,
      workStopTime:
        userDetails &&
        userDetails.workStartTime &&
        userDetails.workDurationMinutes
          ? dayjs(`2018-01-01 ${userDetails.workStartTime}`).add(
              userDetails.workDurationMinutes,
              "minutes"
            )
          : null,
      lunchDurationMinutes:
        userDetails && userDetails.lunchDurationMinutes
          ? userDetails.lunchDurationMinutes
          : null,
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: industryEmployeeValidationSchema,
    onSubmit: (values) => {
      let {
        fullName,
        email,
        phone,
        role,
        salaryType,
        jobTitle,
        workStartTime,
        workStopTime,
        lunchDurationMinutes,
      } = values;
      if (values.salaryType == "HOURLY") {
        if (!values.workStartTime || !values.workStartTime.isValid()) {
          formik.setFieldError("workStartTime", "Invalid Start Time");
          return;
        } else if (!values.workStopTime || !values.workStopTime.isValid()) {
          formik.setFieldError("workStopTime", "Invalid Stop Time");
          return;
        } else {
          let workDurationMinutes = values.workStopTime.diff(
            values.workStartTime,
            "minutes"
          );
          handleSubmit({
            fullName,
            email,
            phone,
            role,
            salaryType,
            jobTitle,
            workStartTime: workStartTime
              ? workStartTime.utc().format("HH:mm Z")
              : null,
            // workStopTime,
            workDurationMinutes,
            lunchDurationMinutes,
          });
        }
      } else {
        handleSubmit({
          fullName,
          email,
          phone,
          role,
          salaryType,
          jobTitle,
          workStartTime: workStartTime
            ? workStartTime.utc().format("HH:mm Z")
            : null,
          // workStopTime,
          workDurationMinutes: null,
          lunchDurationMinutes,
        });
      }
    },
  });

  useEffect(() => {
    console.log(formik.values);
  }, [formik.values]);

  useEffect(() => {
    console.log(formik.errors);
  }, [formik.errors]);

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              required
              fullWidth
              id="fullName"
              label="Full Name"
              name="fullName"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              autoComplete="name"
              onBlur={formik.handleBlur}
              error={
                formik.touched.fullName &&
                formik.errors.fullName &&
                Boolean(formik.errors.fullName && true)
              }
              helperText={
                formik.touched.fullName &&
                formik.errors.fullName &&
                `${formik.errors.fullName}`
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              autoComplete="email"
              onBlur={formik.handleBlur}
              error={
                formik.touched.email &&
                formik.errors.email &&
                Boolean(formik.errors.email && true)
              }
              helperText={
                formik.touched.email &&
                formik.errors.email &&
                `${formik.errors.email}`
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              id="jobTitle"
              label="Job Title"
              name="jobTitle"
              value={formik.values.jobTitle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.jobTitle &&
                formik.errors.jobTitle &&
                Boolean(formik.errors.jobTitle && true)
              }
              helperText={
                formik.touched.jobTitle &&
                formik.errors.jobTitle &&
                `${formik.errors.jobTitle}`
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              label="Phone"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange("phone")}
              onBlur={formik.handleBlur}
              error={
                formik.touched.phone &&
                formik.errors.phone &&
                Boolean(formik.errors.phone && true)
              }
              helperText={
                formik.touched.phone &&
                formik.errors.phone &&
                `${formik.errors.phone}`
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              select
              fullWidth
              required
              label="App Role"
              value={formik.values.role}
              onChange={formik.handleChange("role")}
              onBlur={formik.handleBlur}
              error={
                formik.touched.role &&
                formik.errors.role &&
                Boolean(formik.errors.role && true)
              }
              helperText={
                formik.touched.role &&
                formik.errors.role &&
                `${formik.errors.role}`
              }
            >
              <MenuItem value="EZORDER_ADMIN">Admin</MenuItem>
              <MenuItem value="EZORDER_FOREMAN">Foreman</MenuItem>
              <MenuItem value="EZORDER_EMPLOYEE">Shop</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              select
              fullWidth
              required
              label="Salary Type"
              value={formik.values.salaryType}
              onChange={(e) => {
                let salaryType = e.target.value;
                if (salaryType == "HOURLY") {
                  formik.setValues((prev) => ({
                    ...prev,
                    salaryType,
                    workStartTime: DEFAULT_HOURLY_START_TIME,
                    workStopTime: DEFAULT_HOURLY_STOP_TIME,
                    lunchDurationMinutes: DEFAULT_HOURLY_LUNCH_DURATION,
                  }));
                } else {
                  // SALARY OR EMPTY, clear out the start, stop, lunch
                  formik.setValues((prev) => ({
                    ...prev,
                    salaryType,
                    workStartTime: null,
                    workStopTime: null,
                    lunchDurationMinutes: null,
                  }));
                }
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.salaryType &&
                formik.errors.salaryType &&
                Boolean(formik.errors.salaryType && true)
              }
              helperText={
                formik.touched.salaryType &&
                formik.errors.salaryType &&
                `${formik.errors.salaryType}`
              }
            >
              <MenuItem value="HOURLY">Hourly</MenuItem>
              <MenuItem value="SALARY">Salary</MenuItem>
            </TextField>
          </Grid>
          {formik.values.salaryType == "HOURLY" && (
            <>
              <Grid item xs={12} sm={4}>
                <SelectTime
                  timezone={DEFAULT_TIMEZONE}
                  // onBlur={formik.handleBlur}
                  error={
                    // formik.touched.workStartTime &&
                    formik.errors.workStartTime &&
                    Boolean(formik.errors.workStartTime && true)
                  }
                  helperText={
                    // formik.touched.workStartTime &&
                    formik.errors.workStartTime &&
                    `${formik.errors.workStartTime}`
                  }
                  value={formik.values.workStartTime}
                  label="Start Time"
                  onUpdate={(newValue) => {
                    console.log("Start Time", newValue);
                    formik.setFieldValue("workStartTime", newValue);
                  }}
                  // minTime={dayjs("2018-01-01 0:00 -07:00")}
                  maxTime={formik.values.workStopTime}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <SelectTime
                  timezone={DEFAULT_TIMEZONE}
                  // onBlur={formik.handleBlur}
                  error={
                    // formik.touched.workStartTime &&
                    formik.errors.workStopTime &&
                    Boolean(formik.errors.workStartTime && true)
                  }
                  helperText={
                    // formik.touched.workStopTime &&
                    formik.errors.workStopTime &&
                    `${formik.errors.workStopTime}`
                  }
                  value={formik.values.workStopTime}
                  label="Stop Time"
                  onUpdate={(newValue) => {
                    console.log(typeof newValue);
                    formik.setFieldValue("workStopTime", newValue);
                  }}
                  minTime={formik.values.workStartTime}
                  // maxTime={dayjs("2018-01-01 23:59 -07:00")}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  id="lunchDurationMinutes"
                  label="Lunch Duration (Minutes)"
                  name="lunchDurationMinutes"
                  value={formik.values.lunchDurationMinutes}
                  onChange={formik.handleChange}
                  autoComplete="name"
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lunchDurationMinutes &&
                    formik.errors.lunchDurationMinutes &&
                    Boolean(formik.errors.lunchDurationMinutes && true)
                  }
                  helperText={
                    formik.touched.lunchDurationMinutes &&
                    formik.errors.lunchDurationMinutes &&
                    `${formik.errors.lunchDurationMinutes}`
                  }
                />
              </Grid>
            </>
          )}
        </Grid>
      </LocalizationProvider>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          marginTop: "16px",
        }}
      >
        <LoadingButton
          loading={loading}
          type="submit"
          variant="outlined"
          sx={{ marginRight: "8px" }}
          onClick={() => {
            onCancel();
          }}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          loading={loading}
          color="success"
          type="submit"
          variant="outlined"
          sx={{}}
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          Save
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default IndustryEmployeeForm;
